import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { DataContext } from "../context/DataContext";
import LoadingScreen from "../utilities/LoadingScreen";
import {
  FiUsers,
  FiUserCheck,
  FiShield,
  FiAlertCircle,
  FiCalendar,
  FiSearch,
  FiCheck,
  FiClock,
  FiRotateCcw,
} from "react-icons/fi";

const apiDomain = process.env.REACT_APP_API_DOMAIN;

// Single top-right toast factory. Used everywhere in this page so success /
// error feedback has one consistent look + position. Auto-dismisses at 3s
// and pauses on hover so admins can read it even mid-click.
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (el) => {
    el.addEventListener("mouseenter", Swal.stopTimer);
    el.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

const formatDateTime = (value) => {
  if (!value) return "N/A";
  try {
    const d = new Date(value);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500];
const DEFAULT_PAGE_SIZE = 25;

const UserApproval = () => {
  const { usersInfo, sponsors, loading: contextLoading } = useContext(DataContext);
  const [requests, setRequests] = useState([]); // activation requests for current tab
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  // approvalKind switches the WHOLE page between onboarding-level and
  // event-level approvals. Onboarding hits /user-activation/*, event-level
  // hits /event-participation/* (new endpoints, Trishul + Sitharth
  // 2026-05-26 plan).
  const [approvalKind, setApprovalKind] = useState("onboarding");
  // Tab badge counts per kind so the user sees pending workload on both
  // tabs at a glance.
  const [eventPendingCount, setEventPendingCount] = useState(0);
  const [eventApprovedCount, setEventApprovedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [approvingUserId, setApprovingUserId] = useState(null);
  // Pagination — resets when tab / type / search / page-size changes.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Headline counts that are independent of the currently visible tab so the
  // summary cards stay truthful regardless of which list is in `requests`.
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);

  // Normalise usersInfo into a flat array
  const getUserList = useCallback(() => {
    let list = [];
    if (usersInfo) {
      if (Array.isArray(usersInfo)) list = [...usersInfo];
      else if (Array.isArray(usersInfo.users)) list = [...usersInfo.users];
      else if (Array.isArray(usersInfo.data)) list = [...usersInfo.data];
    }
    if (sponsors) {
      if (Array.isArray(sponsors)) list = [...list, ...sponsors];
      else if (Array.isArray(sponsors.data)) list = [...list, ...sponsors.data];
    }
    return list;
  }, [usersInfo, sponsors]);

  const userList = getUserList();

  // Match by user_id from either side, ignoring any provider prefix
  // (e.g. "instagram:25495..." vs raw "25495..." should still match).
  const stripPrefix = (s) => {
    const str = String(s || "");
    const colon = str.indexOf(":");
    return colon === -1 ? str : str.slice(colon + 1);
  };
  const userById = useCallback(
    (userId) => {
      if (!userId) return null;
      const target = stripPrefix(userId);
      return (
        userList.find((u) => {
          const uidStr =
            u.user_id || u.id || u.userId || u.sponsorId || u.providerId || "";
          return stripPrefix(uidStr) === target;
        }) || null
      );
    },
    [userList]
  );
  const buildHeaders = () => {
    const accessToken = Cookies.get("fbauthToken") || Cookies.get("authToken");
    return {
      "Content-Type": "application/json",
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
  };

  const loadRequests = useCallback(
    async (status = "pending", kind = "onboarding") => {
      if (!apiDomain) return;
      // Clear the table immediately so stale rows from the previous tab
      // (onboarding when we just switched to event-level, or pending when
      // switching to approved, etc.) don't bleed through while the new
      // API call is in flight. Trishul flagged 2026-05-26: switching
      // tabs showed wrong rows + "Event undefined" because the old
      // onboarding rows didn't have event_id but the Event column was
      // rendering regardless.
      setRequests([]);
      setLoading(true);
      setError(null);
      try {
        const url = kind === "event"
          ? `${apiDomain}/api/v2/admin/event-participation/requests`
          : `${apiDomain}/api/v2/admin/user-activation/requests`;
        const res = await axios.get(url, {
          params: { status },
          headers: buildHeaders(),
        });
        // Event endpoint returns {total, rows}; onboarding returns array
        // directly (or {requests}). Normalise.
        let rows;
        if (Array.isArray(res.data)) {
          rows = res.data;
        } else if (Array.isArray(res.data?.rows)) {
          rows = res.data.rows;
        } else if (Array.isArray(res.data?.requests)) {
          rows = res.data.requests;
        } else {
          rows = [];
        }
        setRequests(rows);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || "Failed to load approval requests"
        );
        setRequests([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Fetch all counts independently so the summary cards + tab badges stay
  // truthful even when the visible tab only shows one slice. Cheap —
  // pending/approved on both kinds in parallel.
  const refreshCounts = useCallback(async () => {
    if (!apiDomain) return;
    const sizeOf = (data) => {
      if (Array.isArray(data)) return data.length;
      if (Array.isArray(data?.rows)) return data.rows.length;
      if (Array.isArray(data?.requests)) return data.requests.length;
      return 0;
    };
    try {
      const [pendOnb, apprOnb, pendEvent, apprEvent] = await Promise.all([
        axios.get(`${apiDomain}/api/v2/admin/user-activation/requests`, {
          params: { status: "pending" },
          headers: buildHeaders(),
        }),
        axios.get(`${apiDomain}/api/v2/admin/user-activation/requests`, {
          params: { status: "approved" },
          headers: buildHeaders(),
        }),
        axios.get(`${apiDomain}/api/v2/admin/event-participation/requests`, {
          params: { status: "pending" },
          headers: buildHeaders(),
        }),
        axios.get(`${apiDomain}/api/v2/admin/event-participation/requests`, {
          params: { status: "approved" },
          headers: buildHeaders(),
        }),
      ]);
      setPendingCount(sizeOf(pendOnb.data));
      setApprovedCount(sizeOf(apprOnb.data));
      setEventPendingCount(sizeOf(pendEvent.data));
      setEventApprovedCount(sizeOf(apprEvent.data));
    } catch (_) {
      // Non-fatal — counts simply won't refresh.
    }
  }, []);

  // Counts that reflect the currently selected approval kind so the
  // inner Pending/Approved sub-tabs + summary cards always match the
  // active context. Trishul flagged 2026-05-26: switching to Event-level
  // was leaving the inner counts on onboarding (4/26).
  const activePendingCount =
    approvalKind === "event" ? eventPendingCount : pendingCount;
  const activeApprovedCount =
    approvalKind === "event" ? eventApprovedCount : approvedCount;

  const approveUser = async (userId, eventId) => {
    if (!userId || !apiDomain) return;
    const adminId = Cookies.get("userId") || Cookies.get("adminEmail") || "admin";
    setApprovingUserId(userId);
    setError(null);
    try {
      if (approvalKind === "event") {
        await axios.post(
          `${apiDomain}/api/v2/admin/event-participation/approve`,
          { user_id: userId, event_id: eventId, admin_id: adminId },
          { headers: buildHeaders() }
        );
        setRequests((prev) =>
          prev.filter((r) => !(r.user_id === userId && r.event_id === eventId))
        );
        Toast.fire({ icon: "success", title: "Event participation approved" });
      } else {
        await axios.post(
          `${apiDomain}/api/v2/admin/user-activation/approve`,
          { user_id: userId, admin_id: adminId },
          { headers: buildHeaders() }
        );
        setRequests((prev) => prev.filter((r) => r.user_id !== userId));
        Toast.fire({ icon: "success", title: "User activated" });
      }
      refreshCounts();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to approve request";
      setError(msg);
      Toast.fire({ icon: "error", title: msg });
    } finally {
      setApprovingUserId(null);
    }
  };

  const revertUser = async (userId) => {
    if (!userId || !apiDomain) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Revert user to pending?",
      text: "They will lose access until re-approved.",
      showCancelButton: true,
      confirmButtonText: "Yes, revert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d97706", // amber-600 — matches the orange Revert button
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    setApprovingUserId(userId);
    setError(null);
    try {
      await axios.post(
        `${apiDomain}/api/v2/admin/user-activation/revert`,
        { user_id: userId },
        { headers: buildHeaders() }
      );
      // Drop from the current (Approved) list. The user will reappear when the
      // operator switches to the Pending tab.
      setRequests((prev) => prev.filter((r) => r.user_id !== userId));
      refreshCounts();
      Toast.fire({ icon: "success", title: "Reverted to pending" });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to revert user activation";
      setError(msg);
      Toast.fire({ icon: "error", title: msg });
    } finally {
      setApprovingUserId(null);
    }
  };

  useEffect(() => {
    loadRequests(statusFilter, approvalKind);
  }, [statusFilter, approvalKind, loadRequests]);

  // Always fetch both counts on mount and whenever the tab changes (since the
  // user might have approved / reverted something on the other tab).
  useEffect(() => {
    refreshCounts();
  }, [statusFilter, approvalKind, refreshCounts]);

  // Reset pagination whenever the filter / search input changes so the user
  // doesn't end up on an empty page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, approvalKind, typeFilter, searchTerm, pageSize]);

    // Authoritative sponsor flag: server-set `is_sponsor` first (most
    // accurate), then sponsorName from either side as a fallback.
    const isSponsorRow = (req, user) =>
      Boolean(req.is_sponsor || user?.sponsorName || req.sponsorName);

    const filteredRequests = requests.filter((req) => {
      const user = userById(req.user_id);
      const isSponsorReq = isSponsorRow(req, user);
      if (typeFilter === "user" && isSponsorReq) return false;
      if (typeFilter === "sponsor" && !isSponsorReq) return false;
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        req.user_id?.toLowerCase().includes(term) ||
        (user?.name || req.name || "").toLowerCase().includes(term) ||
        (user?.username || req.username || "").toLowerCase().includes(term) ||
        (user?.sponsorName || req.sponsorName || "").toLowerCase().includes(term) ||
        (user?.email || req.email || "").toLowerCase().includes(term)
      );
    });

    // Per-tab type counts so the User / Sponsor chips can show numbers
    // independent of the active filter.
    const tabTypeCounts = requests.reduce(
      (acc, req) => {
        const user = userById(req.user_id);
        if (isSponsorRow(req, user)) acc.sponsor += 1;
        else acc.user += 1;
        return acc;
      },
      { user: 0, sponsor: 0 }
    );

    // Pagination — derived from the filtered list. Clamp page in case the user
    // is on (say) page 5 and the count shrinks after an approve or they
    // bumped the page-size selector to a value that collapses pages.
    const totalFiltered = filteredRequests.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * pageSize;
    const pageItems = filteredRequests.slice(pageStart, pageStart + pageSize);

    if (contextLoading && !usersInfo) {
      return <LoadingScreen />;
    }
  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center gap-2">
            <FiUserCheck />
            <span>
              {approvalKind === "event"
                ? "Event Participation Approvals"
                : "User Onboarding Approvals"}
            </span>
          </h2>
          <p className="text-gray-600 mt-1">
            {approvalKind === "event"
              ? "Review event participation requests submitted by users without a referral code."
              : "Review onboarding activation requests and activate users."}
          </p>
        </div>
      </div>

      {/* Approval kind tabs — Onboarding vs Event-level. Top-level switch
          driving which endpoint the rest of the page hits. */}
      <div className="mb-4 inline-flex rounded-lg bg-white shadow-sm border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setApprovalKind("onboarding")}
          className={`px-5 py-2 text-sm font-semibold flex items-center gap-1.5 border-r border-gray-200 ${
            approvalKind === "onboarding"
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          Onboarding
          <span
            className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              approvalKind === "onboarding"
                ? "bg-white/25 text-white"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {pendingCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setApprovalKind("event")}
          className={`px-5 py-2 text-sm font-semibold flex items-center gap-1.5 ${
            approvalKind === "event"
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          Event-level
          <span
            className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              approvalKind === "event"
                ? "bg-white/25 text-white"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {eventPendingCount}
          </span>
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="inline-flex rounded-lg bg-white shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setStatusFilter("pending")}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-r border-gray-200 ${
              statusFilter === "pending"
                ? "bg-indigo-500 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <FiClock />
            Pending
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                statusFilter === "pending"
                  ? "bg-white/25 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {activePendingCount}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter("approved")}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${
              statusFilter === "approved"
                ? "bg-indigo-500 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <FiCheck />
            Approved
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                statusFilter === "approved"
                  ? "bg-white/25 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {activeApprovedCount}
            </span>
          </button>
        </div>


        <div className="inline-flex rounded-lg bg-white shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setTypeFilter("all")}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-r border-gray-200 ${
              typeFilter === "all" ? "bg-indigo-500 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            All
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                typeFilter === "all"
                  ? "bg-white/25 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {requests.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setTypeFilter("user")}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-r border-gray-200 ${
              typeFilter === "user" ? "bg-indigo-500 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Users
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                typeFilter === "user"
                  ? "bg-white/25 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {tabTypeCounts.user}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setTypeFilter("sponsor")}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${
              typeFilter === "sponsor" ? "bg-indigo-500 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Sponsors
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                typeFilter === "sponsor"
                  ? "bg-white/25 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {tabTypeCounts.sponsor}
            </span>
          </button>
        </div>

        <div className="flex-1 md:max-w-sm relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by user ID, name, or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          />
        </div>
      </div>

      {/* Summary cards — reflect the active approvalKind. Dim them
          briefly while loading so the count change feels intentional. */}
      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 transition-opacity ${
          loading ? "opacity-50" : "opacity-100"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-yellow-50 text-yellow-600 p-2 rounded-lg">
            <FiClock />
          </div>
          <div>
            <p className="text-sm text-gray-500">Pending Requests</p>
            <p className="text-2xl font-bold text-gray-800">{activePendingCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-indigo-50 text-indigo-600 p-2 rounded-lg">
            <FiCheck />
          </div>
          <div>
            <p className="text-sm text-gray-500">Approved Requests</p>
            <p className="text-2xl font-bold text-gray-800">{activeApprovedCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3">
          <div className="bg-green-50 text-green-600 p-2 rounded-lg">
            <FiShield />
          </div>
          <div>
            <p className="text-sm text-gray-500">View Only</p>
            <p className="text-xs text-gray-500">
              is_active is controlled by backend; this panel only triggers approvals.
            </p>
          </div>
        </div>
      </div>

      {/* Requests table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FiUsers />
            <span>
              {statusFilter === "pending" ? "Pending" : "Approved"}{" "}
              {approvalKind === "event" ? "Event Participation" : "Onboarding"}{" "}
              Requests
            </span>
          </h3>
          {loading && (
            <span className="text-xs text-gray-500">Loading...</span>
          )}
        </div>

        {loading ? (
          // Show a spinner while the API call is in flight so admins
          // see clear "working" feedback instead of a flash of empty
          // state during tab switches (Trishul 2026-05-26).
          <div className="py-12 flex items-center justify-center text-gray-500">
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-200 border-t-indigo-600"
                role="status"
                aria-label="Loading"
              />
              <span className="text-sm">Loading requests…</span>
            </div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <p>
              No {statusFilter}{" "}
              {approvalKind === "event" ? "event participation" : "onboarding"}{" "}
              requests found.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  {approvalKind === "event" && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event
                    </th>
                  )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instagram
                    </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  {statusFilter === "approved" && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Approved At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Approved By
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </>
                  )}
                  {statusFilter === "pending" && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pageItems.map((req) => {
                  const user = userById(req.user_id) || {};
                  // Prefer denormalised fields written on the activation
                  // request itself (populated server-side at create time)
                  // before falling back to the userinformation lookup.
                  // Users that signed up but never persisted a
                  // userinformation row would otherwise show as
                  // "Unknown User" with empty contact info.
                  const displayName =
                    req.name ||
                    user.name ||
                    req.username ||
                    user.username ||
                    user.displayName ||
                    req.fullName ||
                    user.fullName ||
                    user.sponsorName ||
                    req.sponsorName ||
                    "Unknown User";
                  const displayEmail = req.email || user.email || "";
                  const displayMobile = req.mobile_no || user.mobile_no || "";
                  return (
                    <tr
                      key={
                        approvalKind === "event"
                          ? `${req.event_id}-${req.user_id}`
                          : req.user_id
                      }
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-3">
                          {req.profile_picture_url ? (
                            <img
                              src={req.profile_picture_url}
                              alt={displayName}
                              className="w-9 h-9 rounded-full object-cover ring-1 ring-gray-200 flex-shrink-0"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                // IG profile URLs are short-lived signed
                                // CDN URLs — when expired they 403. Replace
                                // with the initial-letter placeholder
                                // instead of leaving a broken-image icon.
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextSibling) {
                                  e.currentTarget.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 font-medium items-center justify-center flex-shrink-0"
                            style={{ display: req.profile_picture_url ? "none" : "flex" }}
                          >
                            {(displayName || "?").trim()[0]?.toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-gray-800 truncate">
                              {displayName}
                            </span>
                            <span className="text-xs text-gray-500 font-mono break-all">
                              {req.user_id}
                            </span>
                          </div>
                        </div>
                      </td>
                      {approvalKind === "event" && (
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col">
                            <span className="font-medium text-indigo-700">
                              {req.event_name || `Event ${req.event_id}`}
                            </span>
                            <span className="text-xs text-gray-500 font-mono">
                              id {req.event_id}
                            </span>
                          </div>
                        </td>
                      )}
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {(() => {
                            const igUsername =
                              req.username ||
                              user.instagramUser?.username ||
                              req.instagramUser?.username ||
                              user.instagram_username ||
                              "";
                            if (igUsername) {
                              return (
                                <a
                                  href={`https://instagram.com/${igUsername}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700 underline"
                                >
                                  @{igUsername}
                                </a>
                              );
                            }
                            // No username on file. IG biz-account ids
                            // aren't directly resolvable via instagram.com
                            // URLs, but a Business-Suite deep-link does
                            // work for admins logged into Meta. We also
                            // expose copy-id + Graph-API explorer links so
                            // the admin can verify the id manually.
                            const uid = String(req.user_id || user.user_id || "");
                            const bareId = uid.toLowerCase().startsWith("instagram:")
                              ? uid.slice(uid.indexOf(":") + 1)
                              : uid;
                            if (/^\d{10,}$/.test(bareId)) {
                              const businessUrl = `https://business.facebook.com/latest/instagram_accounts?asset_id=${bareId}`;
                              return (
                                <div className="flex flex-col gap-0.5">
                                  <a
                                    href={businessUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 underline text-xs"
                                    title="Open in Meta Business Suite (requires admin access)"
                                  >
                                    Business Suite ↗
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(bareId);
                                    }}
                                    className="text-gray-500 font-mono text-[10px] text-left hover:text-gray-700"
                                    title="Click to copy id"
                                  >
                                    id {bareId}
                                  </button>
                                </div>
                              );
                            }
                            return "N/A";
                          })()}
                        </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="space-y-1">
                          {displayEmail && <p>{displayEmail}</p>}
                          {displayMobile && (
                            <p className="text-xs text-gray-500">{displayMobile}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiCalendar className="text-gray-400" />
                          <span>{formatDateTime(req.requested_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                        <p className="line-clamp-2" title={req.note}>
                          {req.note || "—"}
                        </p>
                      </td>
                      {statusFilter === "approved" && (
                        <>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <FiCalendar className="text-gray-400" />
                              <span>{formatDateTime(req.approved_at)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {req.approved_by || "—"}
                          </td>
                          <td className="px-4 py-3 text-right text-sm">
                            <button
                              type="button"
                              onClick={() => revertUser(req.user_id)}
                              disabled={approvingUserId === req.user_id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Revert this user back to pending"
                            >
                              <FiRotateCcw />
                              {approvingUserId === req.user_id
                                ? "Reverting..."
                                : "Revert"}
                            </button>
                          </td>
                        </>
                      )}
                      {statusFilter === "pending" && (
                        <td className="px-4 py-3 text-right text-sm">
                          <button
                            type="button"
                            onClick={() => approveUser(req.user_id, req.event_id)}
                            disabled={approvingUserId === req.user_id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiCheck />
                            {approvingUserId === req.user_id ? "Approving..." : "Approve"}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer — only when something is paginatable. */}
        {totalFiltered > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50/50 text-sm">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-gray-600">
                Showing{" "}
                <span className="font-medium text-gray-800">
                  {pageStart + 1}
                  {"–"}
                  {Math.min(pageStart + pageSize, totalFiltered)}
                </span>{" "}
                of <span className="font-medium text-gray-800">{totalFiltered}</span>
              </span>
              <div className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <label htmlFor="page-size-select">Rows:</label>
                <select
                  id="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="bg-white border border-gray-200 rounded-md py-1 pl-2 pr-6 text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium"
              >
                ← Prev
              </button>
              {/* Compact page indicator. We collapse the middle with an
                  ellipsis once totalPages > 7 so 500-rows-per-page on a
                  3000-row dataset doesn't print a 6-page strip; the surrounding
                  Prev/Next + the numeric input below handle deep navigation. */}
              {(() => {
                const pages = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  // 1 ... around-current ... last
                  const around = new Set([1, totalPages, safePage]);
                  if (safePage > 1) around.add(safePage - 1);
                  if (safePage < totalPages) around.add(safePage + 1);
                  const sorted = Array.from(around).sort((a, b) => a - b);
                  for (let i = 0; i < sorted.length; i++) {
                    pages.push(sorted[i]);
                    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
                      pages.push(`…${i}`);
                    }
                  }
                }
                return pages.map((p) =>
                  typeof p === "string" ? (
                    <span
                      key={p}
                      className="px-1 text-gray-400 text-xs select-none"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={`min-w-[32px] px-2 py-1.5 rounded-lg text-xs font-semibold border ${
                        p === safePage
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "bg-white border-gray-200 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  )
                );
              })()}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserApproval;
